// Per-session outbound-network allow-list.
//
// Enforces the Guest zero-network invariant from
// `.claude/CLAUDE.md §2` + P-01.3 (device-first, cloud-optional): a
// Guest session must not make any outbound network calls. The
// allow-list is empty by default; provider adapters (M3 Google Drive,
// future) call `addAllowedOrigin` at sign-in to register the hosts
// they need. `clearAllowedOrigins` runs on sign-out or session end.
//
// Origins are compared exactly as produced by `new URL(url).origin`
// (scheme + host + port, no trailing slash). Sub-path matching is out
// of scope — a permitted origin is trusted for every path under it.
// Origins deeper than that (e.g. path-scoped allow-lists) belong to
// the adapter, not this module.

const allowedOrigins = new Set<string>();

/**
 * Registers an origin as allowed for outbound `fetch()` calls.
 * Idempotent — re-adding the same origin is a no-op. Called by
 * provider adapters at sign-in.
 *
 * The origin argument is normalised via `new URL(origin).origin`, so
 * callers may pass a full URL (`'https://www.googleapis.com/drive/v3'`)
 * or an origin (`'https://www.googleapis.com'`) — both resolve to the
 * same allow-list entry.
 *
 * @throws {TypeError} If the argument cannot be parsed as a URL.
 */
export function addAllowedOrigin(origin: string): void {
  const normalised = new URL(origin).origin;
  allowedOrigins.add(normalised);
}

/**
 * Removes an origin from the allow-list. Idempotent — removing an
 * origin that is not present is a no-op. Called by provider adapters
 * at sign-out.
 *
 * The origin argument is normalised the same way as `addAllowedOrigin`.
 */
export function removeAllowedOrigin(origin: string): void {
  const normalised = new URL(origin).origin;
  allowedOrigins.delete(normalised);
}

/**
 * Empties the allow-list. Called on session end (tab close, sign-out
 * from the last provider). Also used by tests between cases to
 * guarantee an empty-allow-list starting state.
 */
export function clearAllowedOrigins(): void {
  allowedOrigins.clear();
}

/**
 * Returns `true` iff the URL's origin is on the allow-list.
 *
 * Callers pass either an absolute URL string, a `URL` object, or a
 * relative path resolved against the current document. The URL is
 * evaluated at call time — no caching — so an allow-list mutation
 * takes effect on the next `isAllowed` call.
 */
export function isAllowed(url: URL): boolean {
  return allowedOrigins.has(url.origin);
}

/**
 * Snapshot of the current allow-list. Returned as a new `Set` so
 * callers cannot mutate the internal state. Primary use is debug
 * logging and test assertions.
 */
export function getAllowedOrigins(): ReadonlySet<string> {
  return new Set(allowedOrigins);
}
